import { createStyles, makeStyles } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import classnames from "classnames";
import { format } from "date-fns";
import * as React from "react";

import { RelodeTheme } from "..";

const useStyles = makeStyles((theme: RelodeTheme) =>
    createStyles({
        datePicker: {
            margin: 0,
        },
    })
);

export type DateInputProps = {
    id?: string;
    name: string;
    label: string;
    value?: string | Date | number;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    disableClearable?: boolean;
    onChange?: (value?: string) => void;
    showTodayButton?: boolean;
    className?: string;
    disableFuture?: boolean;
    disablePast?: boolean;
    fullWidth?: boolean;
};

export const DateInput = (props: DateInputProps) => {
    const {
        id,
        name,
        label,
        value,
        onChange,
        error,
        helperText,
        disabled,
        disableClearable,
        disableFuture,
        disablePast,
        showTodayButton,
        className,
        fullWidth,
    } = props;
    const classes = useStyles({});

    const [dateValue, setDateValue] = React.useState<Date>(null);
    const dataCy = props["data-cy"] ? props["data-cy"] : "dateInput";

    React.useEffect(() => {
        // assume value is a string for now.  might not even need it to be a date type.
        let newValue: Date = null;
        if (typeof value === "number" && !isNaN(value)) {
            // value is a number so it is/should be a createTimestamp
            newValue = new Date(value);
            setDateValue(newValue);
            return;
        }
        if (value instanceof Date) {
            setDateValue(value);
            return;
        }

        if (!!value && typeof value === "string") {
            const valueWithTime = /\d{1,2}:\d{1,2}:\d{2}(.\d{3}Z)*$/.test(value)
                ? value
                : value + "T12:00:00";
            newValue = new Date(valueWithTime);
        }

        setDateValue(newValue);
    }, [value]);

    const handleMobileDateChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        onChange?.(event.target.value);
    };

    const handleDesktopDateChange = (_date: Date, _value: string) => {
        let newValue = _value;
        if (!!_date && _date.toString() !== "Invalid Date") {
            newValue = format(_date, "yyyy-MM-dd");
        }
        onChange?.(newValue);
    };

    return (
        <KeyboardDatePicker
            label={label}
            data-cy={dataCy}
            className={classnames(classes.datePicker, className)}
            error={error}
            helperText={helperText}
            format="MM/dd/yyyy"
            clearable={!disableClearable}
            autoOk={true}
            showTodayButton={showTodayButton}
            placeholder="mm/dd/yyyy"
            InputLabelProps={{
                shrink: true,
            }}
            inputProps={{
                "data-cy": dataCy + "-input",
            }}
            value={dateValue}
            onChange={handleDesktopDateChange}
            disabled={disabled}
            disableFuture={disableFuture}
            disablePast={disablePast}
            fullWidth={fullWidth}
        />
    );
};
